import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import DateTimePicker from '../components/DateTimePicker';
import {
  PlusIcon,
  TrashIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  DocumentIcon,
  FolderIcon,
  EyeIcon,
  ArrowPathIcon,
  PencilIcon,
  ClipboardDocumentIcon,
  CameraIcon,
  PrinterIcon,
  FolderPlusIcon,
  DocumentDuplicateIcon,
  PlusCircleIcon,
  ArrowRightCircleIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  LockOpenIcon,
  ClockIcon,
  TagIcon,
  ArrowsRightLeftIcon,
  UserIcon,
  XMarkIcon,
  EllipsisVerticalIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { usePageTitle } from '../contexts/PageTitleContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { useUser } from '../contexts/UserContext';
import { useAuth } from '../contexts/AuthContext';
import { apiService, Policy, FilePermissions, FolderPermissions as ApiFolderPermissions } from '../services/api';
import TagManager from '../components/TagManager';
import { usePasskeyChallenge } from '../hooks/usePasskeyChallenge';
import { useCompanyPolicySettings } from '../hooks/useCompanyPolicySettings';
import { useAssetProtectionSettings } from '../hooks/useAssetProtectionSettings';
import { useTranslation } from 'react-i18next';

const AssetEdit: React.FC = () => {
  const { t } = useTranslation('assetEdit');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useUser();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const { setPageTitle } = usePageTitle();
  const { setHasUnsavedChanges, setOnSave, setIsSubmitting } = useUnsavedChanges();
  const { isFilePermissionVisible, isFolderPermissionVisible } = useCompanyPolicySettings();
  const { isEmailAllowedForOwnershipTransfer, isEmailAllowedForRecipient } = useAssetProtectionSettings();

  // Get file data from navigation state (passed from Assets page)
  const passedFileData = location.state?.fileData;

  // Clear policy cache when file ID changes to ensure fresh data
  React.useEffect(() => {
    if (id) {
      console.log('🔄 ASSET EDIT: File ID changed, clearing policy cache');
      queryClient.invalidateQueries({ queryKey: ['policy'] });
    }
  }, [id, queryClient]);

  const [newRecipient, setNewRecipient] = useState('');
  const [accessLimitationsEnabled, setAccessLimitationsEnabled] = useState(false);
  const [accessCountEnabled, setAccessCountEnabled] = useState(false);
  const [timeLimitationType, setTimeLimitationType] = useState<'days' | 'datetime'>('days');
  const [daysValue, setDaysValue] = useState<number | ''>('');
  const [daysUnit, setDaysUnit] = useState<'day' | 'week' | 'month' | 'year'>('day');
  const [datetimeErrors, setDatetimeErrors] = useState<{
    from?: string;
    to?: string;
  }>({});
  const [datetimeFrom, setDatetimeFrom] = useState<string>('');
  const [datetimeTo, setDatetimeTo] = useState<string>('');
  const [localRecipients, setLocalRecipients] = useState<string[]>([]);
  const [isAddingRecipient, setIsAddingRecipient] = useState(false);
  const [isEditingRecipientMessage, setIsEditingRecipientMessage] = useState(false);
  const [recipientMessage, setRecipientMessage] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isTogglingBlock, setIsTogglingBlock] = useState(false);
  const [isDeletingFile, setIsDeletingFile] = useState(false);
  const [updatingRecipients, setUpdatingRecipients] = useState<Set<string>>(new Set());
  const [showChangeOwner, setShowChangeOwner] = useState(false);
  const [newOwnerEmail, setNewOwnerEmail] = useState('');
  const [isChangingOwner, setIsChangingOwner] = useState(false);
  const [openRecipientMenu, setOpenRecipientMenu] = useState<string | null>(null);
  const [isExportingKey, setIsExportingKey] = useState(false);
  const [tagRefreshKey, setTagRefreshKey] = useState(0);
  const [chipsTagCount, setChipsTagCount] = useState<number | null>(null);

  // Close recipient menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking inside a menu
      if (!target.closest('.recipient-menu-dropdown') && !target.closest(`button[title="${t('tooltips.moreOptions')}"]`)) {
        setOpenRecipientMenu(null);
      }
    };

    if (openRecipientMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openRecipientMenu, t]);

  // Passkey challenge hook for access blocking
  const accessBlockingChallenge = usePasskeyChallenge('access_blocking');

  // Fetch current user's preferences to check OwnershipTransferEnabled
  const { data: currentUserPreferences } = useQuery({
    queryKey: ['user-preferences'],
    queryFn: () => apiService.getUserPreferences(),
    enabled: !!profile?.id,
  });

  // Always fetch file-specific data to get proper recipient emails
  const { data: assetData, isLoading: assetLoading, refetch: refetchAsset } = useQuery({
    queryKey: ['asset', id],
    queryFn: () => apiService.getFileById(id!),
    enabled: !!id,
  });

  // Prioritize fetched data over passed data to ensure UI reflects latest changes
  const asset = assetData?.file || passedFileData;
  const recipients = useMemo(() => assetData?.recipients || [], [assetData?.recipients]);

  // Fetch asset permissions data using the asset's asset_permissions_id
  const { data: assetPermissionsData, isLoading: assetPermissionsLoading, error: assetPermissionsError } = useQuery({
    queryKey: ['asset-permissions', asset?.asset_permissions_id],
    queryFn: () => apiService.getAssetPermissionsById(asset!.asset_permissions_id),
    enabled: !!asset?.asset_permissions_id,
    staleTime: 0, // Always consider data stale to force fresh fetch
    gcTime: 0, // Don't cache the data (gcTime replaces cacheTime)
  });

  const isLoading = assetPermissionsLoading; // Only show loading for asset permissions
  const error = assetPermissionsError; // Only show error for asset permissions
  const isRecipientsLoading = assetLoading; // Recipients loading state

  // Create policy state for editing
  const [editingPolicy, setEditingPolicy] = useState<Policy>({
    id: asset?.asset_permissions_id || 'mock-asset-permissions-id',
    name: 'Default Policy',
    description: '',
    type: 'user',
    asset_type: 'file',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: profile?.id || '',
    updated_by: profile?.id || '',
    settings: {
      encryption_type: 'AES-256',
      recipient_settings: {
        allowed_domains: [],
        allowed_subdomains: [],
        require_approval: false,
      },
      file_permissions: {
        convert_original: { allowed: false, audit_level: 'basic' },
        save_changes: { allowed: false, audit_level: 'basic' },
        copy_paste: { allowed: false, audit_level: 'basic' },
        screen_capture: { allowed: false, audit_level: 'basic' },
        print: { allowed: false, audit_level: 'basic' },
        share: { allowed: false, audit_level: 'basic' },
      },
      folder_permissions: {
        create: { allowed: false, audit_level: 'basic' },
        copy: { allowed: false, audit_level: 'basic' },
        add: { allowed: false, audit_level: 'basic' },
        move: { allowed: false, audit_level: 'basic' },
        share: { allowed: false, audit_level: 'basic' },
        delete: { allowed: false, audit_level: 'basic' },
      },
      screen_watermark: false,
      print_watermark: false,
      security: {
        auth_on_asset_open: false,
        authentication: {
          two_factor: false,
          biometric: false,
          max_attempts: 3,
        },
        session: {
          timeout_seconds: 1800,
          allow_refresh: true,
        },
        protection: {
          delete_on_unauthorized: false,
          delete_on_limit_exceeded: false,
        },
      },
      access_limitations: {
        access_count_limitation: undefined,
        allowed_access_from: undefined,
        allowed_access_to: undefined,
      },
      delete_on_limit_exceeded: false,
      recipient_message: '',
    },
  });

  const editingPolicyRef = useRef(editingPolicy);

  // Update policy data when asset or asset permissions changes
  React.useEffect(() => {
    if (asset && assetPermissionsData) {
      console.log('🔄 ASSET EDIT: Updating asset permissions data for file:', asset.file_name);
      console.log('📋 ASSET EDIT: Asset Permissions ID:', assetPermissionsData.id);
      console.log('📋 ASSET EDIT: Asset type:', assetPermissionsData.asset_type);
      console.log('👥 ASSET EDIT: Recipient settings from backend:', assetPermissionsData.settings?.recipient_settings);
      console.log('📊 ASSET EDIT: Access limitations from backend:', assetPermissionsData.settings?.access_limitations);
      console.log('👥 ASSET EDIT: File recipients from backend:', recipients);
      
      // Set access limitations enabled based on asset permissions data
      // Set time-based limitations enabled if EITHER allowed_access_from OR allowed_access_to is set
      const hasTimeLimitations = !!(assetPermissionsData.settings?.access_limitations && (
        (assetPermissionsData.settings.access_limitations.allowed_access_from !== null &&
         assetPermissionsData.settings.access_limitations.allowed_access_from !== undefined) ||
        (assetPermissionsData.settings.access_limitations.allowed_access_to !== null &&
         assetPermissionsData.settings.access_limitations.allowed_access_to !== undefined)
      ));
      setAccessLimitationsEnabled(hasTimeLimitations);
      
      // Set access count enabled if there's a count limitation
      const hasCountLimitation = !!(assetPermissionsData.settings?.access_limitations?.access_count_limitation);
      setAccessCountEnabled(hasCountLimitation);
      
      // Set recipient message from settings
      setRecipientMessage(assetPermissionsData.settings?.recipient_message || '');
      
      // Set time limitation type based on what's configured
      if (assetPermissionsData.settings?.access_limitations?.allowed_access_from || assetPermissionsData.settings?.access_limitations?.allowed_access_to) {
        setTimeLimitationType('datetime');
        // Set datetime values for display
        if (assetPermissionsData.settings.access_limitations.allowed_access_from) {
          const fromDate = new Date(assetPermissionsData.settings.access_limitations.allowed_access_from);
          const year = fromDate.getFullYear();
          const month = String(fromDate.getMonth() + 1).padStart(2, '0');
          const day = String(fromDate.getDate()).padStart(2, '0');
          const hours = String(fromDate.getHours()).padStart(2, '0');
          const minutes = String(fromDate.getMinutes()).padStart(2, '0');
          setDatetimeFrom(`${year}-${month}-${day}T${hours}:${minutes}`);
        }
        if (assetPermissionsData.settings.access_limitations.allowed_access_to) {
          const toDate = new Date(assetPermissionsData.settings.access_limitations.allowed_access_to);
          const year = toDate.getFullYear();
          const month = String(toDate.getMonth() + 1).padStart(2, '0');
          const day = String(toDate.getDate()).padStart(2, '0');
          const hours = String(toDate.getHours()).padStart(2, '0');
          const minutes = String(toDate.getMinutes()).padStart(2, '0');
          setDatetimeTo(`${year}-${month}-${day}T${hours}:${minutes}`);
        }
        
      } else {
        setTimeLimitationType('days');
        setDaysValue('');
      }
      
      // Populate recipients from file data
      // Use recipient IDs for local state management
      if (recipients && recipients.length > 0) {
        const recipientIds = recipients.map((recipient: any) => recipient.id);
        setLocalRecipients(recipientIds);
        console.log('👥 ASSET EDIT: Loaded recipients from file:', recipientIds);
        console.log('👥 ASSET EDIT: Full recipient data:', recipients);
      } else {
        setLocalRecipients([]);
        console.log('👥 ASSET EDIT: No recipients found for file');
      }
      
      setEditingPolicy({
        id: assetPermissionsData.id || '',
        name: 'Asset Permissions', // Asset permissions don't have names, use generic name
        description: 'Asset-specific permissions', // Asset permissions don't have descriptions
        type: 'user', // Asset permissions are always user type
        asset_type: assetPermissionsData.asset_type || 'file',
        status: 'active', // Asset permissions are always active
        created_at: assetPermissionsData.created_at || new Date().toISOString(),
        updated_at: assetPermissionsData.updated_at || new Date().toISOString(),
        created_by: assetPermissionsData.created_by || '',
        updated_by: assetPermissionsData.created_by || '', // Use created_by as updated_by
        settings: {
          encryption_type: assetPermissionsData.settings?.encryption_type || 'AES-256',
          recipient_settings: assetPermissionsData.settings?.recipient_settings || {
            allowed_domains: [],
            allowed_subdomains: [],
            require_approval: false,
          },
          file_permissions: assetPermissionsData.settings?.file_permissions || {
            convert_original: { allowed: false, audit_level: 'basic' },
            save_changes: { allowed: false, audit_level: 'basic' },
            copy_paste: { allowed: false, audit_level: 'basic' },
            screen_capture: { allowed: false, audit_level: 'basic' },
            print: { allowed: false, audit_level: 'basic' },
            share: { allowed: false, audit_level: 'basic' },
          },
          folder_permissions: assetPermissionsData.settings?.folder_permissions || {
            create: { allowed: false, audit_level: 'basic' },
            copy: { allowed: false, audit_level: 'basic' },
            add: { allowed: false, audit_level: 'basic' },
            move: { allowed: false, audit_level: 'basic' },
            share: { allowed: false, audit_level: 'basic' },
            delete: { allowed: false, audit_level: 'basic' },
          },
          screen_watermark: typeof assetPermissionsData.settings?.screen_watermark === 'boolean'
            ? assetPermissionsData.settings.screen_watermark
            : (assetPermissionsData.settings?.screen_watermark as any)?.enabled ?? false,
          print_watermark: typeof assetPermissionsData.settings?.print_watermark === 'boolean'
            ? assetPermissionsData.settings.print_watermark
            : (assetPermissionsData.settings?.print_watermark as any)?.enabled ?? false,
          security: assetPermissionsData.settings?.security || {
            auth_on_asset_open: false,
            authentication: {
              two_factor: false,
              biometric: false,
              max_attempts: 3,
            },
            session: {
              timeout_seconds: 1800,
              allow_refresh: true,
            },
            protection: {
              delete_on_unauthorized: assetPermissionsData.settings?.security?.protection?.delete_on_unauthorized || false,
              delete_on_limit_exceeded: assetPermissionsData.settings?.security?.protection?.delete_on_limit_exceeded ?? false,
            },
          },
          access_limitations: assetPermissionsData.settings?.access_limitations || {
            access_count_limitation: undefined,
            allowed_access_from: undefined,
            allowed_access_to: undefined,
          },
          delete_on_limit_exceeded: assetPermissionsData.settings?.delete_on_limit_exceeded ?? false,
          recipient_message: assetPermissionsData.settings?.recipient_message || '',
        },
      });
    }
  }, [asset, assetPermissionsData, recipients]);

  // Set page title immediately if we have passed data, otherwise when asset data is loaded
  useEffect(() => {
    if (asset) {
      setPageTitle(t('title'), t('subtitle'));
    }
  }, [asset, setPageTitle, t]);

  // Save changes mutation - updating the asset permissions
  const saveChangesMutation = useMutation({
    mutationFn: async (policyData: Policy) => {
      // Transform frontend data structure to match backend expectations
      const backendAssetPermissionsData = {
        settings: {
          encryption_type: policyData.settings.encryption_type,
          recipient_settings: policyData.settings.recipient_settings,
          file_permissions: policyData.settings.file_permissions,
          folder_permissions: policyData.settings.folder_permissions,
          screen_watermark: policyData.settings.screen_watermark,
          print_watermark: policyData.settings.print_watermark,
          security: policyData.settings.security,
          access_limitations: policyData.settings.access_limitations,
          delete_on_limit_exceeded: policyData.settings.delete_on_limit_exceeded,
          recipient_message: policyData.settings.recipient_message,
        }
      };
      
      console.log('Updating asset permissions with backend structure:', backendAssetPermissionsData);
      return apiService.updateAssetPermissions(policyData.id, backendAssetPermissionsData.settings);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-permissions', asset?.asset_permissions_id] });
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
      queryClient.invalidateQueries({ queryKey: ['files'] });
      setHasUnsavedChanges(false);
      setIsSubmitting(false);
      toast.success(t('toast.permissionsUpdated'));
    },
    onError: (error: any) => {
      setIsSubmitting(false);
      toast.error(error.response?.data?.message || t('toast.permissionsFailed'));
    },
  });

  // Helper function to validate email address
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to check if email is allowed by domain/subdomain rules
  const isEmailAllowed = (email: string): { allowed: boolean; reason?: string } => {
    const domain = email.split('@')[1];
    if (!domain) return { allowed: false, reason: 'Invalid email format' };

    const allowedDomains = settings.recipient_settings?.allowed_domains || [];
    const allowedSubdomains = settings.recipient_settings?.allowed_subdomains || [];

    // If no domain restrictions are set (empty arrays), allow any domain
    if (allowedDomains.length === 0 && allowedSubdomains.length === 0) {
      return { allowed: true };
    }

    // Check if domain matches any allowed domain
    if (allowedDomains.includes(domain)) {
      return { allowed: true };
    }

    // Check if domain matches any allowed subdomain
    if (allowedSubdomains.includes(domain)) {
      return { allowed: true };
    }

    // Check if domain is a subdomain of any allowed domain
    for (const allowedDomain of allowedDomains) {
      if (domain.endsWith('.' + allowedDomain)) {
        return { allowed: true };
      }
    }

    return { 
      allowed: false, 
      reason: `Email domain '${domain}' is not in the allowed domains or subdomains list` 
    };
  };

  const addRecipient = async () => {
    if (!newRecipient.trim() || !asset) return;
    
    const trimmedValue = newRecipient.trim();
    
    // Validate email format
    if (!isValidEmail(trimmedValue)) {
      toast.error(t('toast.validEmail'));
      return;
    }

    // Check if email is already in the list
    if (localRecipients.includes(trimmedValue)) {
      toast.error(t('toast.emailAlreadyInList'));
      return;
    }

    // Check if email is allowed by domain/subdomain rules
    const { allowed, reason } = isEmailAllowed(trimmedValue);
    if (!allowed) {
      toast.error(reason || t('toast.emailNotAllowed'));
      return;
    }

    // Check asset protection restrictions for recipients
    // Note: Frontend validation is lenient - backend will perform definitive validation
    const recipientValidation = isEmailAllowedForRecipient(trimmedValue.toLowerCase());
    if (!recipientValidation.allowed) {
      toast.error(recipientValidation.reason || t('toast.companyRestrictions'));
      return;
    }

    setIsAddingRecipient(true);
    try {
      // Add recipient via API
      const response = await apiService.addRecipient(asset.id, {
        type: 'email',
        value: trimmedValue,
        // times_accessible now comes from AssetPermissions (backend auto-populates)
        is_external: true,
      });

      setLocalRecipients(prev => [...prev, response.id]);
      setNewRecipient('');
      toast.success(t('toast.recipientAdded'));
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || t('toast.recipientAddFailed');
      toast.error(errorMessage);
    } finally {
      setIsAddingRecipient(false);
    }
  };

  const removeRecipient = async (recipientId: string) => {
    if (!asset) return;

    setUpdatingRecipients(prev => new Set(prev).add(recipientId));
    try {
      // Remove recipient via API
      await apiService.removeRecipient(asset.id, recipientId);

      // Update local state - remove by recipient ID
      setLocalRecipients(prev => prev.filter(r => r !== recipientId));
    toast.success(t('toast.recipientRemoved'));

      // Refresh file data
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('toast.recipientRemoveFailed'));
    } finally {
      setUpdatingRecipients(prev => {
        const next = new Set(prev);
        next.delete(recipientId);
        return next;
      });
    }
  };

  // Handle toggling block status
  const handleToggleBlock = async () => {
    if (!asset) return;

    const success = await accessBlockingChallenge.executeWithChallenge(async () => {
    setIsTogglingBlock(true);
    try {
      const newStatus = asset.status === 'blocked' ? 'ready' : 'blocked';
      await apiService.updateFileStatus(asset.id, newStatus);
      
      toast.success(
        newStatus === 'blocked' ? t('toast.fileBlocked') : t('toast.fileUnblocked'),
        { duration: 3000 }
      );
      
      // Refresh file data immediately
      await refetchAsset();
      queryClient.invalidateQueries({ queryKey: ['files'] });
    } catch (error: any) {
      console.error('Error toggling file status:', error);
      toast.error(error.response?.data?.error || t('toast.fileStatusFailed'), { duration: 4000 });
        throw error;
    } finally {
      setIsTogglingBlock(false);
      }
    });

    if (!success) {
      // Passkey challenge failed or was cancelled
      return;
    }
  };

  // Handle changing owner
  const handleChangeOwner = async () => {
    if (!asset || !newOwnerEmail.trim()) return;

    const trimmedEmail = newOwnerEmail.trim().toLowerCase();

    // Validate email format
    if (!isValidEmail(trimmedEmail)) {
      toast.error(t('toast.validEmail'));
      return;
    }

    // Check asset protection restrictions for ownership transfer
    // Note: Frontend validation is lenient - backend will perform definitive validation
    const ownershipValidation = isEmailAllowedForOwnershipTransfer(trimmedEmail);
    if (!ownershipValidation.allowed) {
      toast.error(ownershipValidation.reason || t('toast.ownershipRestrictions'));
      return;
    }

    setIsChangingOwner(true);
    try {
      const result = await apiService.changeOwner([asset.id], trimmedEmail);

      if (result.success_count > 0) {
        toast.success(t('toast.ownerChanged'), {
          duration: 4000,
          style: {
            background: '#10B981',
            color: 'white',
          },
        });

        setShowChangeOwner(false);
        setNewOwnerEmail('');
        
        // Refresh asset data
        await refetchAsset();
        queryClient.invalidateQueries({ queryKey: ['files'] });
      } else {
        toast.error(t('toast.ownerFailed'), {
          duration: 5000,
        });
      }
    } catch (error: any) {
      console.error('Error changing owner:', error);
      toast.error(error.response?.data?.error || t('toast.ownerFailedError'), {
        duration: 4000,
      });
    } finally {
      setIsChangingOwner(false);
    }
  };

  // Handle deleting file
  const handleExportKey = async () => {
    if (!asset) return;
    const userIdRaw =
      profile?.role === 'CompanyAdmin' && asset.user_id && asset.user_id !== profile.id
        ? asset.user_id
        : undefined;
    const userId: string | undefined = typeof userIdRaw === 'string' ? userIdRaw : undefined;
    setIsExportingKey(true);
    try {
      const { blob, filename } = await apiService.exportFileKey('raw', asset.id, userId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t('toast.exportKey'));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('toast.exportFailed'));
    } finally {
      setIsExportingKey(false);
    }
  };

  const handleDeleteFile = async () => {
    if (!asset) return;

    setIsDeletingFile(true);
    try {
      await apiService.deleteFile(asset.id);
      
      toast.success(t('toast.fileDeleted'), { duration: 4000 });
      
      // Navigate back to assets page
      setShowDeleteConfirm(false);
      setTimeout(() => {
        navigate('/assets');
      }, 500);
    } catch (error: any) {
      console.error('Error deleting file:', error);
      toast.error(error.response?.data?.error || t('toast.fileDeleteFailed'), { duration: 4000 });
      setIsDeletingFile(false);
    }
  };

  const updatePermission = (section: string, permission: string, value: boolean | number | string | undefined) => {
    if (!asset) return;
    
    setEditingPolicy(prev => {
      const updated = { ...prev };
      
      if (section === 'file_permissions') {
        if (updated.settings.file_permissions) {
          updated.settings.file_permissions = {
            ...updated.settings.file_permissions,
            [permission]: { 
              allowed: value as boolean, 
              audit_level: updated.settings.file_permissions[permission as keyof FilePermissions]?.audit_level || 'basic' 
            },
          };
        }
      } else if (section === 'folder_permissions') {
        if (updated.settings.folder_permissions) {
          updated.settings.folder_permissions = {
            ...updated.settings.folder_permissions,
            [permission]: { 
              allowed: value as boolean, 
              audit_level: updated.settings.folder_permissions[permission as keyof ApiFolderPermissions]?.audit_level || 'basic' 
            },
          };
        }
      } else if (section === 'security') {
        if (permission === 'auth_on_asset_open') {
          updated.settings.security = {
            ...updated.settings.security,
            auth_on_asset_open: value as boolean,
          };
        } else if (permission === 'two_factor') {
          updated.settings.security.authentication = {
            two_factor: value as boolean,
            biometric: updated.settings.security.authentication?.biometric || false,
            max_attempts: updated.settings.security.authentication?.max_attempts || 3,
          };
        } else if (permission === 'biometric') {
          updated.settings.security.authentication = {
            two_factor: updated.settings.security.authentication?.two_factor || false,
            biometric: value as boolean,
            max_attempts: updated.settings.security.authentication?.max_attempts || 3,
          };
        } else if (permission === 'max_attempts') {
          updated.settings.security.authentication = {
            two_factor: updated.settings.security.authentication?.two_factor || false,
            biometric: updated.settings.security.authentication?.biometric || false,
            max_attempts: value as number,
          };
        } else if (permission === 'timeout_seconds') {
          updated.settings.security.session = {
            timeout_seconds: value as number,
            allow_refresh: updated.settings.security.session?.allow_refresh || true,
          };
        } else if (permission === 'delete_on_unauthorized') {
          updated.settings.security.protection = {
            delete_on_unauthorized: value as boolean,
            delete_on_limit_exceeded: updated.settings.security.protection?.delete_on_limit_exceeded || false,
          };
        } else if (permission === 'delete_on_limit_exceeded') {
          updated.settings.security.protection = {
            delete_on_unauthorized: updated.settings.security.protection?.delete_on_unauthorized || false,
            delete_on_limit_exceeded: value as boolean,
          };
        }
      } else if (section === 'watermark') {
        if (permission === 'screen_enabled') {
          updated.settings.screen_watermark = value as boolean;
        } else if (permission === 'print_enabled') {
          updated.settings.print_watermark = value as boolean;
        }
      } else if (section === 'general') {
        if (permission === 'access_count_limitation') {
          updated.settings.access_limitations = {
            ...updated.settings.access_limitations,
            access_count_limitation: value !== undefined ? (value as number) : undefined,
          };
        } else if (permission === 'allowed_access_from') {
          updated.settings.access_limitations = {
            ...updated.settings.access_limitations,
            allowed_access_from: value as string,
          };
        } else if (permission === 'allowed_access_to') {
          updated.settings.access_limitations = {
            ...updated.settings.access_limitations,
            allowed_access_to: value as string,
          };
        } else if (permission === 'recipient_message') {
          updated.settings.recipient_message = value as string;
        } else if (permission === 'delete_on_limit_exceeded') {
          updated.settings.delete_on_limit_exceeded = value as boolean;
        }
      }

      return updated;
    });
    
    setHasUnsavedChanges(true);
  };

  const handleSaveChangesRef = useRef<(() => void) | undefined>(undefined);
  
  const handleSaveChanges = useCallback(async () => {
    saveChangesMutation.mutate(editingPolicyRef.current);
  }, [saveChangesMutation]);

  // Update refs when dependencies change
  useEffect(() => {
    editingPolicyRef.current = editingPolicy;
    handleSaveChangesRef.current = handleSaveChanges;
  }, [editingPolicy, handleSaveChanges]);

  // Sync recipient message state with editing policy
  useEffect(() => {
    if (recipientMessage !== editingPolicy.settings.recipient_message) {
      setEditingPolicy(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          recipient_message: recipientMessage
        }
      }));
    }
  }, [recipientMessage, editingPolicy.settings.recipient_message]);

  // Set the save function for the header
  useEffect(() => {
    setOnSave(() => () => handleSaveChangesRef.current?.());
    return () => setOnSave(null);
  }, [setOnSave]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      addRecipient();
    }
  };

  // Handle time limitation type change
  const handleTimeLimitationTypeChange = (type: 'days' | 'datetime') => {
    setTimeLimitationType(type);
    
    if (type === 'days') {
      // Clear datetime values when switching to days
      setEditingPolicy(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          access_limitations: {
            ...prev.settings.access_limitations,
            allowed_access_from: undefined,
            allowed_access_to: undefined,
          }
        }
      }));
      setDatetimeFrom('');
      setDatetimeTo('');
    } else {
      // Clear days value when switching to datetime
      setDaysValue('');
      setDaysUnit('day');
      // Also clear the calculated datetime values from days
      setEditingPolicy(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          access_limitations: {
            ...prev.settings.access_limitations,
            allowed_access_from: undefined,
            allowed_access_to: undefined,
          }
        }
      }));
    }
  };

  // Calculate datetime values from days
  const calculateDateTimeFromDays = (days: number, unit: 'day' | 'week' | 'month' | 'year') => {
    const now = new Date();
    let daysToAdd = days;
    
    switch (unit) {
      case 'week':
        daysToAdd = days * 7;
        break;
      case 'month':
        daysToAdd = days * 30; // Approximate
        break;
      case 'year':
        daysToAdd = days * 365; // Approximate
        break;
      default:
        daysToAdd = days;
    }
    
    const fromDate = new Date(now);
    const toDate = new Date(now.getTime() + (daysToAdd * 24 * 60 * 60 * 1000));
    
    return {
      from: fromDate.toISOString(),
      to: toDate.toISOString()
    };
  };

  // Update datetime values when days change
  useEffect(() => {
    if (timeLimitationType === 'days' && accessLimitationsEnabled && daysValue !== '' && daysValue > 0) {
      const { from, to } = calculateDateTimeFromDays(daysValue as number, daysUnit);
      setEditingPolicy(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          access_limitations: {
            ...prev.settings.access_limitations,
            allowed_access_from: from,
            allowed_access_to: to,
          }
        }
      }));
    } else if (timeLimitationType === 'days' && accessLimitationsEnabled && (daysValue === '' || daysValue <= 0)) {
      // If days value is empty or invalid, clear datetime values
      setEditingPolicy(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          access_limitations: {
            ...prev.settings.access_limitations,
            allowed_access_from: undefined,
            allowed_access_to: undefined,
          }
        }
      }));
    }
  }, [daysValue, daysUnit, timeLimitationType, accessLimitationsEnabled]);

  // Validate datetime values (for editing - no past date validation)
  const validateDatetimeValues = (fromValue?: string, toValue?: string) => {
    const errors: { from?: string; to?: string } = {};
    
    // Only validate relative dates (from < to), not past dates during editing
    if (fromValue && toValue) {
      const fromDate = new Date(fromValue);
      const toDate = new Date(toValue);
      if (fromDate >= toDate) {
        errors.to = t('validation.toAfterFrom');
      }
    }
    
    setDatetimeErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Toggle Switch Component
  const ToggleSwitch: React.FC<{
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
  }> = ({ checked, onChange, disabled = false }) => (
    <button
      type="button"
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
        checked ? 'bg-green-600' : 'bg-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  // Show loading state while loading asset permissions
  if (isLoading) {
    // Check if user is sysadmin or company admin
    const isSysAdmin = profile?.role === 'SysAdmin';
    const isCompanyAdmin = profile?.role === 'CompanyAdmin';
    const hasElevatedAccess = isSysAdmin || isCompanyAdmin;
    
    return (
      <div className="space-y-6">
      

        {/* Loading message */}
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            <h3 className="mt-6 text-lg font-medium text-gray-900">
              {hasElevatedAccess 
                ? isSysAdmin 
                  ? t('loading.retrievingAsSystemAdmin')
                  : t('loading.retrievingAsCompanyAdmin')
                : t('loading.loadingPermissions')}
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {hasElevatedAccess 
                ? t('loading.loadingWithElevatedAccess')
                : t('loading.pleaseWait')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Check user role for access control
  const isSysAdmin = profile?.role === 'SysAdmin';
  const isCompanyAdmin = profile?.role === 'CompanyAdmin';
  const hasElevatedAccess = isSysAdmin || isCompanyAdmin;
  
  // If we don't have data yet and no error, show loading state
  if (!assetPermissionsData && !error) {
    return (
      <div className="space-y-6">
        

        {/* Loading message */}
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            <h3 className="mt-6 text-lg font-medium text-gray-900">
              {hasElevatedAccess 
                ? isSysAdmin 
                  ? t('loading.retrievingAsSystemAdmin')
                  : t('loading.retrievingAsCompanyAdmin')
                : t('loading.loadingPermissions')}
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              {hasElevatedAccess 
                ? t('loading.loadingWithElevatedAccess')
                : t('loading.pleaseWait')}
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Only show error state if there's an actual error AND user is not an admin
  // Admins never see the error - they always get access
  if (error && !hasElevatedAccess) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-amber-500" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('error.insufficientPermissions')}</h3>
        <p className="mt-1 text-sm text-gray-500">{t('error.attemptRecorded')}</p>
      </div>
    );
  }

  const { settings } = editingPolicy;

  // Check if current user is the owner and has ownership transfer enabled (profile is source of truth; fallback to preferences query)
  const isOwner = asset && profile && (
    asset.owner_id === profile.id || 
    asset.user_id === profile.id ||
    asset.owner_email?.toLowerCase() === profile.email?.toLowerCase()
  );
  const ownershipTransferEnabled = currentUser?.ownership_transfer_enabled ?? currentUserPreferences?.preferences?.ownership_transfer_enabled ?? true;
  const canTransferOwnership = isOwner && ownershipTransferEnabled;

  // Helper function to format file name (remove .zip extension for folders)
  const formatFileName = (fileName: string, assetType?: string, mimeType?: string): string => {
    // If this is a folder (by asset_type or mime_type), remove .zip extension from display
    if (assetType?.toLowerCase() === 'folder' || mimeType === 'application/zip') {
      if (fileName.toLowerCase().endsWith('.zip')) {
        return fileName.slice(0, -4); // Remove .zip extension
      }
    }
    return fileName;
  };

  // Get file icon based on extension, asset_type, or mime_type (same as Assets page)
  const getFileIcon = (fileName: string, assetType?: string, mimeType?: string) => {
    // Check if this is a folder (prioritize asset_type, then mime_type)
    // If asset_type is 'folder', always show folder icon (for zipped folders stored as files)
    // If mime_type is 'application/zip', show folder icon (zipped folders)
    if (assetType?.toLowerCase() === 'folder' || mimeType === 'application/zip') {
      return (
        <img
          src="/asset-icons/folder.webp"
          alt="folder"
          className="w-8 h-8 flex-shrink-0 object-contain"
        />
      );
    }

    const extension = fileName.split('.').pop()?.toLowerCase() || 'file';
    const upperExtension = extension.toUpperCase();

    // Map extensions to icon file names (handles variations like docx -> doc)
    const extensionMap: Record<string, string> = {
      'txt': 'txt',
      'doc': 'doc',
      'docx': 'doc',
      'xls': 'xls',
      'xlsx': 'xls',
      'ppt': 'ppt',
      'pptx': 'ppt',
      'pdf': 'pdf',
      'csv': 'csv',
      'png': 'png',
      'jpg': 'jpg',
      'jpeg': 'jpg',
      'gif': 'gif',
      'svg': 'svg',
      'mp4': 'mp4',
      'mp3': 'mp3',
      'zip': 'zip',
      'md': 'md',
      'webloc': 'webloc',
    };

    // Check if we have an icon for this extension
    const iconName = extensionMap[extension];
    if (iconName) {
      return (
        <img
          src={`/asset-icons/${iconName}.webp`}
          alt={`${extension} file`}
          className="w-8 h-8 flex-shrink-0 object-contain"
        />
      );
    }

    // Fallback to text-based icon for unsupported types
    return (
      <div className="w-8 h-8 flex-shrink-0 bg-black dark:bg-gray-800 rounded flex items-center justify-center">
        <span className="text-white text-xs font-bold">{upperExtension}</span>
      </div>
    );
  };

  // Get status circle color
  const getStatusCircle = (status: string) => {
    if (status === 'deleted') {
      return <div className="w-3 h-3 rounded-full bg-gray-400 flex-shrink-0"></div>;
    } else if (status === 'blocked') {
      return <div className="w-3 h-3 rounded-full bg-yellow-500 flex-shrink-0"></div>;
    } else {
      return <div className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"></div>;
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Back Button */}
     
      
      {/* Header Section with File Name and Actions */}
      {asset && (
        <div className="card p-4 md:p-6">
          <div className="flex items-center justify-between gap-2 md:gap-3">
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              {getStatusCircle(asset.status)}
              <div className="scale-75 md:scale-100 origin-left flex-shrink-0">{getFileIcon(asset.file_name, asset.asset_type, assetData?.metadata?.mime_type || asset.metadata?.mime_type)}</div>
              <div className="min-w-0">
                <h1 className="text-base md:text-lg lg:text-2xl font-bold text-gray-900 truncate">{formatFileName(asset.file_name, asset.asset_type, assetData?.metadata?.mime_type || asset.metadata?.mime_type)}</h1>
                {(profile?.role === 'sysadmin' || profile?.role === 'admin') && assetData?.owner_email && (
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                    <UserIcon className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{assetData.owner_email}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Tag input + Action Buttons */}
            <div className="flex flex-shrink-0 items-center gap-2 md:gap-3">
              {/* Tag Input (chips shown in row 2) */}
              <TagManager
                key={tagRefreshKey}
                compact
                showChips={false}
                showInput={true}
                resourceType="asset"
                resourceId={asset.id}
                onTagsChange={() => {
                  setTagRefreshKey(k => k + 1);
                  queryClient.invalidateQueries({ queryKey: ['asset', id] });
                }}
              />
              {/* Activity Log Button */}
        <button
                onClick={() => navigate(`/logs?resource_id=${asset.id}`)}
                className="p-1.5 md:p-2 border-2 border-blue-600 text-blue-600 bg-white hover:bg-blue-50 transition-colors flex items-center justify-center rounded-lg"
                title={t('sections.activityHistory')}
              >
                <ClockIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
              </button>
              
              {/* Change Owner Button - Only show if user is owner and has ownership transfer enabled */}
              {canTransferOwnership && (
                <button
                  onClick={() => setShowChangeOwner(true)}
                  disabled={isChangingOwner}
                  className="p-1.5 md:p-2 border-2 border-black text-black bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-lg"
                  title={t('sections.changeOwner')}
                >
                  <div className="flex items-center gap-0.5 md:gap-1">
                    <ArrowsRightLeftIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                    <UserIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  </div>
                </button>
              )}
              
              {/* Export File Key Button - only when company allows allow_file_key_download (or personal user) */}
              {currentUser?.allow_file_key_download && (
              <button
                onClick={handleExportKey}
                disabled={isExportingKey}
                className="p-1.5 md:p-2 border-2 border-black text-black bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-lg"
                title={t('sections.exportFileKey')}
              >
                <ArrowDownTrayIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
              </button>
              )}

              {/* Block/Unblock Toggle Button */}
              {(asset.status === 'blocked' || asset.status === 'ready') && (
                <button
                  onClick={handleToggleBlock}
                  disabled={isTogglingBlock}
                  className={`p-1.5 md:p-2 border-2 ${
                    asset.status === 'blocked'
                      ? 'border-green-600 text-green-600 hover:bg-green-50'
                      : 'border-yellow-600 text-yellow-600 hover:bg-yellow-50'
                  } bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-lg`}
                  title={asset.status === 'blocked' ? t('sections.unblockAccess') : t('sections.blockAccess')}
                >
                  {isTogglingBlock ? (
                      <div className="animate-spin rounded-full h-3.5 w-3.5 md:h-4 md:w-4 border-b-2 border-current"></div>
                  ) : asset.status === 'blocked' ? (
                      <LockOpenIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  ) : (
                      <LockClosedIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  )}
                </button>
              )}
              
              {/* Delete Button */}
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeletingFile}
                className="p-1.5 md:p-2 border-2 border-red-600 text-red-600 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center rounded-lg"
                title={t('sections.deleteFile')}
              >
                <TrashIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
        </button>
      </div>
          </div>
          {/* Tags — chips row below file name; hidden until tags load and only when count > 0 */}
          <div className={chipsTagCount !== null && chipsTagCount > 0 ? 'mt-3 pt-3 border-t border-gray-100 flex items-center gap-2' : 'hidden'}>
            <TagIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <TagManager
              key={tagRefreshKey}
              compact
              showChips={true}
              showInput={false}
              resourceType="asset"
              resourceId={asset.id}
              onTagCountChange={setChipsTagCount}
              onTagsChange={() => {
                setTagRefreshKey(k => k + 1);
                queryClient.invalidateQueries({ queryKey: ['asset', id] });
              }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Access Limitations */}
          <div className="card p-4 md:p-6 h-full order-1">
            <div className="flex items-center space-x-2 mb-3 md:mb-4">
                <ShieldCheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                <h2 className="text-base md:text-lg font-semibold text-gray-900">{t('sections.accessLimitations')}</h2>
                {(accessCountEnabled || accessLimitationsEnabled || (settings.delete_on_limit_exceeded && (accessCountEnabled || accessLimitationsEnabled))) && (
                  <span className="ml-auto text-xs text-gray-400 font-normal">
                    {[
                      accessCountEnabled && `${settings.access_limitations?.access_count_limitation ?? '∞'} accesses`,
                      accessLimitationsEnabled && 'time limit',
                      settings.delete_on_limit_exceeded && (accessCountEnabled || accessLimitationsEnabled) && 'delete on exceed',
                    ].filter(Boolean).join(' · ')}
                  </span>
                )}
              </div>
            
            <div className="space-y-4 md:space-y-6">
                {/* Access Count Limitation */}
                <div>
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <label className="block text-xs md:text-sm font-medium text-gray-700">
                      {t('sections.accessCountLimitation')}
                    </label>
                <button
                  type="button"
                        onClick={() => {
                          const newValue = !accessCountEnabled;
                          setAccessCountEnabled(newValue);
                          
                          // Update policy state
                          if (!newValue) {
                            // If disabling count, remove access_count_limitation
                            setEditingPolicy(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings,
                                access_limitations: {
                                  ...prev.settings.access_limitations,
                                  access_count_limitation: undefined
                                }
                              }
                            }));
                          }
                          setHasUnsavedChanges(true);
                        }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                          accessCountEnabled ? 'bg-governate-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            accessCountEnabled ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
            </div>
            
                  {accessCountEnabled && (
                    <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    value={settings.access_limitations?.access_count_limitation || ''}
                    onChange={(e) => {
                      if (e.target.value === '') {
                        // Empty input means unlimited - set to undefined
                        updatePermission('general', 'access_count_limitation', undefined);
                      } else {
                        const value = parseInt(e.target.value);
                        if (value && value > 0) {
                          updatePermission('general', 'access_count_limitation', value);
                        }
                      }
                    }}
                        className="w-20 md:w-24 px-2 md:px-3 py-1.5 md:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        placeholder=""
                    min="1"
                    step="1"
                  />
                      <span className="text-xs md:text-sm text-gray-500">{t('sections.times')}</span>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {t('sections.accessCountHint')}
                  </p>
                </div>

                {/* Time-based Limitations */}
                <div>
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <label className="block text-xs md:text-sm font-medium text-gray-700">
                      Time-based Limitations
                    </label>
                    <button
                        type="button"
                        onClick={() => {
                          const newValue = !accessLimitationsEnabled;
                          setAccessLimitationsEnabled(newValue);
                          
                          // Update policy state
                          if (!newValue) {
                            // If disabling, clear time-based access limitations
                            setEditingPolicy(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings,
                                access_limitations: {
                                  ...prev.settings.access_limitations,
                                  allowed_access_from: undefined,
                                  allowed_access_to: undefined
                                }
                              }
                            }));
                          }
                          setHasUnsavedChanges(true);
                        }}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                          accessLimitationsEnabled ? 'bg-governate-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                            accessLimitationsEnabled ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                  </div>
                  {accessLimitationsEnabled && (
                    <div className="space-y-3 md:space-y-4">
                    {/* Radio buttons */}
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="timeOption"
                          value="days"
                          checked={timeLimitationType === 'days'}
                          onChange={() => {
                            handleTimeLimitationTypeChange('days');
                            setHasUnsavedChanges(true);
                          }}
                          className="mr-2"
                        />
                        <span className="text-xs md:text-sm text-gray-700">{t('sections.numberOfDays')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="timeOption"
                          value="datetime"
                          checked={timeLimitationType === 'datetime'}
                          onChange={() => {
                            handleTimeLimitationTypeChange('datetime');
                            setHasUnsavedChanges(true);
                          }}
                          className="mr-2"
                        />
                        <span className="text-xs md:text-sm text-gray-700">{t('sections.specificDateRange')}</span>
                      </label>
                    </div>

                    {/* Days input - only show when days is selected */}
                    {timeLimitationType === 'days' && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={daysValue}
                          onChange={(e) => {
                            const value = e.target.value;
                            setDaysValue(value === '' ? '' : parseInt(value) || '');
                            setHasUnsavedChanges(true);
                          }}
                          className="w-18 md:w-20 px-2 md:px-3 py-1.5 md:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                          min="1"
                          step="1"
                        />
                        <select
                          value={daysUnit}
                          onChange={(e) => {
                            setDaysUnit(e.target.value as 'day' | 'week' | 'month' | 'year');
                            setHasUnsavedChanges(true);
                          }}
                          className="px-2 md:px-3 py-1.5 md:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        >
                          <option value="day">day(s)</option>
                          <option value="week">week(s)</option>
                          <option value="month">month(s)</option>
                          <option value="year">year(s)</option>
                        </select>
                        <span className="text-xs md:text-sm text-gray-500">from creation</span>
              </div>
                    )}

                    {/* Date/Time Pickers - only show when datetime is selected */}
                    {timeLimitationType === 'datetime' && (() => {
                      const now = new Date();
                      const fromDate = settings.access_limitations?.allowed_access_from 
                        ? new Date(settings.access_limitations.allowed_access_from) 
                        : null;
                      const toDate = settings.access_limitations?.allowed_access_to 
                        ? new Date(settings.access_limitations.allowed_access_to) 
                        : null;
                      
                      // Determine status for Access From
                      let fromStatus: 'active' | 'inactive' | null = null;
                      let fromTooltip = '';
                      if (fromDate) {
                        if (fromDate > now) {
                          fromStatus = 'inactive';
                          fromTooltip = t('tooltips.fromFuture');
                        } else {
                          fromStatus = 'active';
                          fromTooltip = t('tooltips.fromPast');
                        }
                      }
                      
                      // Determine status for Access To
                      let toStatus: 'active' | 'inactive' | null = null;
                      let toTooltip = '';
                      if (toDate) {
                        if (toDate > now) {
                          toStatus = 'active';
                          toTooltip = t('tooltips.toFuture');
                        } else {
                          toStatus = 'inactive';
                          toTooltip = t('tooltips.toPast');
                        }
                      }
                      
                      return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                        <div>
                            <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                              <span>{t('sections.accessFrom')}</span>
                              {fromStatus && (
                                <div className="group relative">
                                  <div className={`w-2 h-2 rounded-full ${
                                    fromStatus === 'active' ? 'bg-green-500' : 'bg-red-500'
                                  }`} />
                                  <div className="absolute left-0 top-6 z-10 hidden group-hover:block">
                                    <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap shadow-lg">
                                      {fromTooltip}
                                      <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                                    </div>
                                  </div>
                                </div>
                              )}
                          </label>
                          <div className="relative">
                            <DateTimePicker
                              value={datetimeFrom || null}
                              onChange={(value) => {
                                if (value) {
                                  setDatetimeFrom(value);
                                  updatePermission('general', 'allowed_access_from', value);
                                    const toValue = settings.access_limitations?.allowed_access_to;
                                  validateDatetimeValues(value, toValue);
                                } else {
                                  setDatetimeFrom('');
                                  updatePermission('general', 'allowed_access_from', undefined);
                                  setDatetimeErrors(prev => ({ ...prev, from: undefined }));
                                }
                              }}
                              showTimeSelect={true}
                              timeIntervals={1}
                              dateFormat="yyyy-MM-dd HH:mm"
                              returnFormat="iso"
                              placeholder={t('placeholders.datetime')}
                              className={datetimeErrors.from ? 'border-red-300 bg-red-50 dark:bg-red-950' : ''}
                              popperPlacement="bottom-end"
                            />
                          </div>
                          {datetimeErrors.from && (
                            <p className="mt-1 text-xs md:text-sm text-red-600 dark:text-red-400">{datetimeErrors.from}</p>
                          )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                              <span>{t('sections.accessTo')}</span>
                              {toStatus && (
                                <div className="group relative">
                                  <div className={`w-2 h-2 rounded-full ${
                                    toStatus === 'active' ? 'bg-green-500' : 'bg-red-500'
                                  }`} />
                                  <div className="absolute left-0 top-6 z-10 hidden group-hover:block">
                                    <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap shadow-lg">
                                      {toTooltip}
                                      <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                                    </div>
                                  </div>
                                </div>
                              )}
                          </label>
                          <div className="relative">
                            <DateTimePicker
                              value={datetimeTo || null}
                              onChange={(value) => {
                                if (value) {
                                  setDatetimeTo(value);
                                  updatePermission('general', 'allowed_access_to', value);
                                    const fromValue = settings.access_limitations?.allowed_access_from;
                                  validateDatetimeValues(fromValue, value);
                                } else {
                                  setDatetimeTo('');
                                  updatePermission('general', 'allowed_access_to', undefined);
                                  setDatetimeErrors(prev => ({ ...prev, to: undefined }));
                                }
                              }}
                              showTimeSelect={true}
                              timeIntervals={1}
                              dateFormat="yyyy-MM-dd HH:mm"
                              returnFormat="iso"
                              placeholder={t('placeholders.datetime')}
                              className={datetimeErrors.to ? 'border-red-300 bg-red-50 dark:bg-red-950' : ''}
                            />
                          </div>
                          {datetimeErrors.to && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{datetimeErrors.to}</p>
                          )}
                        </div>
                      </div>
                      );
                    })()}
                    </div>
                  )}

                </div>

                {/* Delete on Limit Exceeded */}
                <div>
                  <div className="flex items-center justify-between mb-2 md:mb-3">
                    <div>
                      <label className={`block text-xs md:text-sm font-medium ${!accessCountEnabled && !accessLimitationsEnabled ? 'text-gray-400' : 'text-gray-700'}`}>
                        Delete file from recipient if exceeded
                      </label>
                      {!accessCountEnabled && !accessLimitationsEnabled && (
                        <p className="text-xs text-gray-400 italic mt-0.5">Enable an access limit to use this option</p>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={!accessCountEnabled && !accessLimitationsEnabled}
                      onClick={() => {
                        updatePermission('general', 'delete_on_limit_exceeded', !settings.delete_on_limit_exceeded);
                      }}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
                        settings.delete_on_limit_exceeded && (accessCountEnabled || accessLimitationsEnabled)
                          ? 'bg-governate-600'
                          : 'bg-gray-200'
                      } ${!accessCountEnabled && !accessLimitationsEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                          settings.delete_on_limit_exceeded && (accessCountEnabled || accessLimitationsEnabled) ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

</div>
          </div>

          {/* Policy Recipients Section */}
          <div className="card p-4 md:p-6 order-3">
            <div className="flex items-center space-x-2 mb-3 md:mb-4">
              <UserGroupIcon className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
              <h2 className="text-base md:text-lg font-semibold text-gray-900">{t('sections.recipients')}</h2>
              {recipients && recipients.length > 0 && (
                <span className="ml-auto text-xs text-gray-400 font-normal">
                  {recipients.length} {recipients.length === 1 ? 'recipient' : 'recipients'}
                </span>
              )}
            </div>
            
            {/* Current Recipients */}
            <div className="mb-4">
              {isRecipientsLoading ? (
                // Show skeleton while recipients are loading
                <div className="animate-pulse">
                  <div className="flex items-center justify-between py-3 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                      <div className="w-7 h-7 bg-gray-200 rounded-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-48"></div>
                    </div>
                    <div className="w-4 h-4 bg-gray-200 rounded"></div>
                    </div>
                </div>
              ) : (
                <>
                  {recipients?.map((recipient: any, index: number) => {
                    const timesAccessible: number | null = recipient.times_accessible ?? null;
                    const totalAccesses = recipient.total_accesses ?? 0;
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const _availableAccesses = timesAccessible != null ? Math.max(0, timesAccessible - totalAccesses) : null;
                    const isUpdating = updatingRecipients.has(recipient.id);

                    return (
                    <div 
                      key={recipient.id} 
                      className={`flex items-center justify-between py-2.5 ${
                        index !== recipients.length - 1 ? 'border-b border-gray-100' : ''
                      } hover:bg-gray-50 transition-colors relative`}
                    >
                        <div className="flex items-center space-x-2 md:space-x-3 flex-1 min-w-0">
                          <p className="text-xs md:text-sm font-medium text-gray-900 truncate">{recipient.value}</p>
                        </div>
                        {/* Desktop: Show all actions */}
                        <div className="hidden md:flex items-center space-x-2 flex-shrink-0">
                          {/* Access count / limit badge */}
                          {timesAccessible != null && (
                            <span className="text-xs font-medium rounded px-1.5 py-0.5 text-gray-500 bg-gray-100 border border-gray-200">
                              {totalAccesses}/{timesAccessible} used
                            </span>
                          )}
                          <button
                            onClick={() => removeRecipient(recipient.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                            title={t('tooltips.removeRecipient')}
                            disabled={isUpdating}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {/* Mobile: Three dots menu */}
                        <div className="md:hidden relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenRecipientMenu(openRecipientMenu === recipient.id ? null : recipient.id);
                            }}
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                            title={t('tooltips.moreOptions')}
                          >
                            <EllipsisVerticalIcon className="w-5 h-5" />
                          </button>
                          
                          {/* Dropdown Menu */}
                          {openRecipientMenu === recipient.id && (
                            <div className="recipient-menu-dropdown absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                              <div className="py-1">
                                {/* Delete */}
                                <button
                                  onClick={() => {
                                    removeRecipient(recipient.id);
                                    setOpenRecipientMenu(null);
                                  }}
                                  disabled={isUpdating}
                                  className="w-full px-3 md:px-4 py-1.5 md:py-2 text-left text-xs md:text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 md:gap-2"
                                >
                                  <TrashIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                                  <span>{t('folderPermissions.delete')}</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

              {/* Show message if no recipients */}
                  {(!recipients || recipients.length === 0) && (
                    <div className="text-center py-6 text-gray-500">
                  <p className="text-sm">{t('sections.noRecipientsConfigured')}</p>
                </div>
                  )}
                </>
              )}
            </div>

            {/* Add New Recipient */}
            <div className="border-t dark:border-gray-700 pt-3 md:pt-4 flex items-center gap-2">
                <input
                  type="email"
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isAddingRecipient}
                  placeholder={t('placeholders.email')}
                  className="flex-1 px-2 md:px-3 py-1.5 md:py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-governate-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed transition-colors"
                />
                <button
                  onClick={addRecipient}
                  disabled={!newRecipient.trim() || isAddingRecipient}
                  className="px-3 md:px-4 py-1.5 md:py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {isAddingRecipient ? (
                    <div className="animate-spin rounded-full h-3.5 w-3.5 md:h-4 md:w-4 border-b-2 border-gray-600"></div>
                  ) : (
                    <PlusIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  )}
                </button>
                <span
                  className="text-gray-400 hover:text-gray-600 cursor-default flex-shrink-0"
                  title="Enter an email address. The domain must be in the allowed domains or subdomains list."
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <circle cx="12" cy="12" r="10" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4m0-4h.01" />
                  </svg>
                </span>
            </div>
            
            
          </div>
          {/* File Permissions - Only show for file assets */}
          {editingPolicy.asset_type === 'file' && (
            <div className="card p-4 md:p-6 h-full order-2">
              <div className="flex items-center space-x-2 mb-3 md:mb-4">
                <DocumentIcon className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                <h2 className="text-base md:text-lg font-semibold text-gray-900">{t('filePermissions.title')}</h2>
                {(() => {
                  const createdAt = assetPermissionsData?.created_at;
                  const allKeys = ['convert_original','save_changes','copy_paste','screen_capture','screen_watermark','print','print_watermark','share'];
                  const visible = allKeys.filter(k => isFilePermissionVisible(k, createdAt));
                  const allowed = visible.filter(k =>
                    k === 'screen_watermark' ? settings.screen_watermark
                    : k === 'print_watermark' ? settings.print_watermark
                    : settings.file_permissions?.[k as keyof FilePermissions]?.allowed || false
                  ).length;
                  return visible.length > 0 ? (
                    <span className="ml-auto text-xs text-gray-400 font-normal">{allowed} / {visible.length} allowed</span>
                  ) : null;
                })()}
              </div>
              
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 md:gap-y-3">
                {[
                  { key: 'convert_original', label: t('filePermissions.convert_original'), icon: ArrowPathIcon },
                  { key: 'save_changes', label: t('filePermissions.save_changes'), icon: PencilIcon },
                  { key: 'copy_paste', label: t('filePermissions.copy_paste'), icon: ClipboardDocumentIcon },
                  { key: 'screen_capture', label: t('filePermissions.screen_capture'), icon: CameraIcon },
                  { key: 'screen_watermark', label: t('filePermissions.screen_watermark'), icon: EyeIcon },
                  { key: 'print', label: t('filePermissions.print'), icon: PrinterIcon },
                  { key: 'print_watermark', label: t('filePermissions.print_watermark'), icon: PrinterIcon },
                  { key: 'share', label: t('filePermissions.share'), icon: UserGroupIcon },
                ]
                .filter(({ key }) => {
                  const createdAt = assetPermissionsData?.created_at;
                  return isFilePermissionVisible(key, createdAt);
                })
                .map(({ key, label, icon: Icon }) => (
                  <div key={key} className="flex items-center gap-2 min-w-0">
                    <ToggleSwitch
                      checked={key === 'screen_watermark'
                        ? settings.screen_watermark
                        : key === 'print_watermark'
                        ? settings.print_watermark
                        : settings.file_permissions?.[key as keyof FilePermissions]?.allowed || false}
                      onChange={(checked) => {
                        if (key === 'screen_watermark') {
                          updatePermission('watermark', 'screen_enabled', checked);
                        } else if (key === 'print_watermark') {
                          updatePermission('watermark', 'print_enabled', checked);
                        } else {
                          updatePermission('file_permissions', key, checked);
                        }
                      }}
                    />
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Icon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                      <span className="text-xs text-gray-700 leading-tight truncate" title={label}>{label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Folder Permissions - Only show for folder assets */}
          {editingPolicy.asset_type === 'folder' && (
            <div className="card p-4 md:p-6 h-full order-2">
              <div className="flex items-center space-x-2 mb-3 md:mb-4">
                <FolderIcon className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                <h2 className="text-base md:text-lg font-semibold text-gray-900">{t('folderPermissions.title')}</h2>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2 md:gap-y-3">
                {[
                  { key: 'create', label: t('folderPermissions.create'), icon: FolderPlusIcon },
                  { key: 'copy', label: t('folderPermissions.copy'), icon: DocumentDuplicateIcon },
                  { key: 'add', label: t('folderPermissions.add'), icon: PlusCircleIcon },
                  { key: 'move', label: t('folderPermissions.move'), icon: ArrowRightCircleIcon },
                  { key: 'share', label: t('folderPermissions.share'), icon: UserGroupIcon },
                  { key: 'delete', label: t('folderPermissions.delete'), icon: TrashIcon },
                ]
                .filter(({ key }) => {
                  const createdAt = assetPermissionsData?.created_at;
                  return isFolderPermissionVisible(key, createdAt);
                })
                .map(({ key, label, icon: Icon }) => (
                  <div key={key} className="flex items-center gap-2 min-w-0">
                    <ToggleSwitch
                      checked={settings.folder_permissions?.[key as keyof ApiFolderPermissions]?.allowed || false}
                      onChange={(checked) => updatePermission('folder_permissions', key, checked)}
                    />
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Icon className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                      <span className="text-xs text-gray-700 leading-tight truncate" title={label}>{label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recipient Message Section */}
          <div className="card p-4 md:p-6 order-4">
            <div className="flex items-center space-x-2 mb-3 md:mb-4">
              <DocumentIcon className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
              <h2 className="text-base md:text-lg font-semibold text-gray-900">{t('sections.recipientMessage')}</h2>
            </div>

            <div className="space-y-3">

              <p className="text-xs md:text-sm text-gray-500">
                This message will be shown to recipients when they access the file.
              </p>

              {isEditingRecipientMessage ? (
                <div className="space-y-2">
                  <textarea
                    value={recipientMessage}
                    onChange={(e) => {
                      setRecipientMessage(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    onBlur={() => {
                      setIsEditingRecipientMessage(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setRecipientMessage(settings.recipient_message || '');
                        setIsEditingRecipientMessage(false);
                      }
                    }}
                    className="w-full px-2 md:px-3 py-1.5 md:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent resize-none"
                    rows={2}
                    placeholder={t('placeholders.messageForRecipients')}
                    autoFocus
                  />
                </div>
              ) : (
                <div
                  onClick={() => setIsEditingRecipientMessage(true)}
                  className="w-full px-2 md:px-3 py-1.5 md:py-2 text-sm border border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 focus:ring-2 focus:ring-governate-500 focus:border-transparent min-h-[2.5rem] flex items-center"
                >
                  {settings.recipient_message || (
                    <span className="text-xs md:text-sm text-gray-400">Click to add a message for recipients...</span>
                  )}
                </div>
              )}
            </div>
          </div>


            </div>

      {/* Change Owner Modal */}
      {showChangeOwner && asset && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => {
            setShowChangeOwner(false);
            setNewOwnerEmail('');
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('changeOwnerModal.title')}</h3>
              <button
                onClick={() => {
                  setShowChangeOwner(false);
                  setNewOwnerEmail('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                {t('changeOwnerModal.description')} <span className="font-semibold">{formatFileName(asset.file_name, asset.asset_type, assetData?.metadata?.mime_type || asset.metadata?.mime_type)}</span>.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('changeOwnerModal.newOwnerEmail')}
                </label>
                <input
                  type="email"
                  value={newOwnerEmail}
                  onChange={(e) => setNewOwnerEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newOwnerEmail.trim() && !isChangingOwner) {
                      handleChangeOwner();
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                  placeholder={t('placeholders.newOwnerEmail')}
                  autoFocus
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowChangeOwner(false);
                  setNewOwnerEmail('');
                }}
                className="px-4 py-2 border-2 border-black text-black text-sm font-medium bg-white hover:bg-gray-50 transition-colors rounded-lg"
              >
                {t('changeOwnerModal.cancel')}
              </button>
              <button
                onClick={handleChangeOwner}
                disabled={!newOwnerEmail.trim() || isChangingOwner}
                className="px-4 py-2 border-2 border-black text-black text-sm font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 rounded-lg"
              >
                {isChangingOwner ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                    <span>{t('changeOwnerModal.changing')}</span>
                  </>
                ) : (
                  <span>{t('changeOwnerModal.changeOwner')}</span>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && asset && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 border-2 border-red-600 rounded-full flex items-center justify-center">
                <TrashIcon className="w-6 h-6 text-red-600" />
        </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('deleteModal.title')}</h3>
                <p className="text-sm text-gray-500">{t('deleteModal.cannotUndone')}</p>
      </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-700 mb-2">
                {t('deleteModal.confirmPrompt')}
              </p>
              <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                {formatFileName(asset.file_name, asset.asset_type, assetData?.metadata?.mime_type || asset.metadata?.mime_type)}
              </p>
              <div className="mt-3 p-3 bg-white border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium">{t('deleteModal.warning')}</p>
                <ul className="text-sm text-red-700 mt-1 ml-4 list-disc">
                  <li>{t('deleteModal.bullet1')}</li>
                  <li>{t('deleteModal.bullet2')}</li>
                  <li>{t('deleteModal.bullet3')}</li>
                  <li>{t('deleteModal.bullet4')}</li>
                </ul>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeletingFile}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                {t('deleteModal.cancel')}
              </button>
              <button
                onClick={handleDeleteFile}
                disabled={isDeletingFile}
                className="flex-1 px-4 py-2 bg-white text-red-600 border-2 border-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors flex items-center justify-center space-x-2"
              >
                {isDeletingFile ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                    <span>{t('deleteModal.deleting')}</span>
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    <span>{t('deleteModal.delete')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AssetEdit;
